import { useCallback, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'

import { PortalName } from 'commons/types/enums'
import { useChallengeExpiration, useCreateDevice, useResetChallenge, useSendSecurityCode } from '~auth/hooks/mfa'
import { AuthMFAState } from '~auth/types/DTO'

export const useCreation = (portal: PortalName, refreshHandler: () => void) => {
  const [visible, setVisible] = useState(false)
  const open = useCallback(() => setVisible(true), [setVisible])
  const close = useCallback(() => setVisible(false), [setVisible])
  const deviceMutation = useCreateDevice(portal)
  const resetMutation = useResetChallenge()
  const securityCodeMutation = useSendSecurityCode(portal, true)
  const client = useQueryClient()
  const { data: mfa } = useQuery<AuthMFAState | null>({
    queryKey: ['mfa'],
    queryFn: () => client.getQueryData<AuthMFAState>(['mfa']) || null,
    initialData: () => client.getQueryData<AuthMFAState>(['mfa']) || null,
    staleTime: Infinity
  })

  // Start challenge expiration listener.
  useChallengeExpiration(false)

  const createDevice = useCallback(
    (value: { type: string; name: string }) =>
      deviceMutation.mutateAsync({
        type: value.type,
        name: value.name,
        token: ''
      }),
    [deviceMutation]
  )

  const resetChallenge = useCallback(() => {
    resetMutation.mutate()
  }, [resetMutation])

  const retryChallenge = useCallback(
    () =>
      deviceMutation.mutateAsync({
        type: mfa?.chosen?.type || '',
        name: mfa?.chosen?.name || '',
        token: ''
      }),
    [deviceMutation, mfa?.chosen?.name, mfa?.chosen?.type]
  )

  const sendSecurityCode = useCallback(
    async (code: string) => {
      await securityCodeMutation.mutateAsync({
        code,
        challengeId: mfa?.challengeId || '',
        token: ''
      })
      refreshHandler()
    },
    [securityCodeMutation, mfa?.challengeId, refreshHandler]
  )

  return {
    resetChallenge,
    retryChallenge,
    createDevice,
    sendSecurityCode,
    challengeStartedAt: mfa?.challengeStartedAt || 0,
    totpUrl: mfa?.chosen?.totpUrl,
    isCreationDialogOpen: visible,
    openCreationDialog: open,
    closeCreationDialog: close
  }
}
